import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import CapexaraCognitiveSubstrate from "./CapexaraCognitiveSubstrate";
import ArchitectureModal from "./ArchitectureModal";

gsap.registerPlugin(ScrollTrigger);

/* ═══════════════════════════════════════════════════════════
   MAIN SECTION COMPONENT — Cognitive Spine
   Now uses CapexaraCognitiveSubstrate (SVG) instead of R3F
   ═══════════════════════════════════════════════════════════ */
export function CognitiveSpine() {
  const sectionRef = useRef(null);
  const progressRef = useRef(0);
  const demoActiveRef = useRef(false);
  const beatElsRef = useRef([]);
  const progElsRef = useRef([]);
  const [demoPhase, setDemoPhase] = useState(0);
  const [archOpen, setArchOpen] = useState(false);

  useLayoutEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const trigger = ScrollTrigger.create({
      trigger: el,
      start: "top top",
      end: "+=150vh",
      pin: true,
      onUpdate: (self) => { progressRef.current = self.progress; },
    });
    return () => { trigger.kill(); };
  }, []);

  useEffect(() => {
    let prevBeat = -1;
    let raf;
    const loop = () => {
      const p = progressRef.current;
      const beat = p < 0.30 ? 0 : p < 0.63 ? 1 : 2;

      if (beat !== prevBeat) {
        beatElsRef.current.forEach((el, i) => {
          if (!el) return;
          el.classList.toggle("active", i === beat);
        });
        progElsRef.current.forEach((el, i) => {
          if (!el) return;
          el.className = "cs-prog-line" + (i === beat ? " active" : i < beat ? " done" : "");
        });
        prevBeat = beat;
      }

      if (beat === 2 && !demoActiveRef.current) {
        demoActiveRef.current = true;
        setDemoPhase(1);
        setTimeout(() => setDemoPhase(2), 3200);
      }
      if (beat < 2 && demoActiveRef.current) {
        demoActiveRef.current = false;
        setDemoPhase(0);
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section ref={sectionRef} className="cs" id="cognitive-spine" data-testid="cognitive-spine">
      <div className="cs-bg" aria-hidden="true" />
      <div className="cs-grid" aria-hidden="true" />

      {/* Cognitive Substrate Illustration */}
      <div className="cs-canvas" aria-hidden="true">
        <CapexaraCognitiveSubstrate />
      </div>

      {/* Left column copy */}
      <div className="cs-left" data-testid="cs-copy">
        <div className="cs-beats-wrap">
          {/* Beat 1 */}
          <div ref={el => beatElsRef.current[0] = el} className="cs-beat active" data-testid="cs-beat-1">
            <div className="cs-eyebrow"><span className="cs-eyebrow-line" />THE COGNITIVE SPINE</div>
            <h2 className="cs-h2">
              Eight platforms<br />shouldn&rsquo;t mean<br />eight intelligences.
            </h2>
            <p className="cs-sub">
              Most enterprise AI companies bolt chatbots onto disconnected products. Each bot knows only its own silo.
              Context dies at every handoff. You get eight mediocre assistants instead of one capable team.
            </p>
            <p className="cs-meta">&darr; Scroll to see how we architected this differently.</p>
          </div>

          {/* Beat 2 */}
          <div ref={el => beatElsRef.current[1] = el} className="cs-beat" data-testid="cs-beat-2">
            <div className="cs-eyebrow"><span className="cs-eyebrow-line" />THE COGNITIVE SPINE</div>
            <h2 className="cs-h2">
              Meet <span className="cs-sutradhar">Cognara</span>.<br />
              The brain behind<br />every Capexara platform.
            </h2>
            <p className="cs-sub">
              Cognara (&ldquo;the orchestrator&rdquo;) is our
              purpose-trained AI engine &mdash; a multi-agent reasoning system
              fluent in FIDIC, NEC, API, ASME, IS codes, BOCW, and the
              unwritten laws of EPC.
            </p>
            <p className="cs-sub cs-sub-cont">
              Every one of our seven enterprise platforms invokes
              Cognara natively. Luminex uses it to extract processes
              from documents. Docuweave uses it to auto-tag drawings.
              Panorix uses it to detect schedule anomalies. ProcureX uses
              it to draft claims. One brain. Seven expressions.
            </p>
            <div className="cs-stat-row" data-testid="cs-stats">
              <span>2.4B tokens</span>
              <span className="cs-stat-sep">&middot;</span>
              <span>9 specialist agents</span>
              <span className="cs-stat-sep">&middot;</span>
              <span>domain-trained</span>
            </div>
          </div>

          {/* Beat 3 */}
          <div ref={el => beatElsRef.current[2] = el} className="cs-beat" data-testid="cs-beat-3">
            <div className="cs-eyebrow"><span className="cs-eyebrow-line" />THE COGNITIVE SPINE</div>
            <h2 className="cs-h2">
              Ask once.<br />Reason across<br />all eight platforms.
            </h2>
            <p className="cs-sub">
              When a project controls lead asks Panorix &ldquo;why did the
              critical path shift?&rdquo; &mdash; Cognara routes the question
              through four platforms in parallel. It reads the schedule
              from Panorix, the PO status from ProcureX, the drawings from
              Docuweave, and matches the pattern against historical
              precedents in Evalore.
            </p>
            <p className="cs-sub cs-sub-cont">
              One natural-language question. Twelve data points across
              four platforms. One cited, confident answer.<br /><br />
              This is what one brain &mdash; instead of eight &mdash; actually means.
            </p>
            <div className="cs-ctas">
              <Link to="/platforms/cognara" className="cs-btn-primary" data-testid="cs-cta-sutradhar">
                Meet Cognara <span className="cs-arrow">&rarr;</span>
              </Link>
              <button
                className="cs-btn-ghost"
                data-testid="cs-cta-arch"
                onClick={() => setArchOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={archOpen}
              >
                See the architecture
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Beat progress indicator */}
      <div className="cs-progress" data-testid="cs-progress">
        {[0, 1, 2].map(i => (
          <div key={i} ref={el => progElsRef.current[i] = el} className={`cs-prog-line${i === 0 ? " active" : ""}`} />
        ))}
      </div>

      {/* Scroll cue */}
      <div className="cs-scroll-cue" aria-hidden="true">
        <svg width="12" height="20" viewBox="0 0 12 20">
          <path d="M6 2v14M2 12l4 4 4-4" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" />
        </svg>
      </div>

      <ArchitectureModal open={archOpen} onClose={() => setArchOpen(false)} />
    </section>
  );
}
